import type { ReactNode } from 'react';
import { fill } from '../../i18n/shell-texts';
import { useShellTexts } from '../../i18n/use-shell-texts';
import { pageWindow, paginatorState } from './paginator-state';
import type { TablePagination } from './types';

/** One box of a Bootstrap `.pagination` group; neighbours overlap by their 1px border. */
function PageBox({
  label,
  active = false,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <li className="-ml-px first:ml-0">
      <button
        type="button"
        aria-label={label}
        aria-current={active ? 'page' : undefined}
        disabled={disabled}
        onClick={onClick}
        className={`relative block min-w-9 border px-3 py-2 text-center leading-tight [li:first-child>&]:rounded-l-bs [li:last-child>&]:rounded-r-bs ${
          active
            ? 'z-10 border-brand bg-brand text-white'
            : 'border-pager-border bg-white text-brand hover:bg-line hover:text-brand-dark disabled:cursor-not-allowed disabled:bg-white disabled:text-ink-muted'
        }`}
      >
        {children}
      </button>
    </li>
  );
}

/**
 * The table's pager in the legacy look: a group of page boxes (`« ‹ [p-2 … p+2] › »`) on the
 * left and a group of page-size boxes on the right, with a small range text between. Without a
 * total (`totalCount: null`) only `‹ ›` remain — neither the last page nor the numbers are known.
 * A group with nothing to choose from is hidden.
 */
export function TablePaginator({
  pagination,
  pageItemCount = null,
  disabled = false,
  align = 'between',
  formatRange,
  onPageChange,
}: {
  pagination: TablePagination;
  /** Rows on the current page; closes the range when the total is unknown. */
  pageItemCount?: number | null;
  disabled?: boolean;
  /** `center`: the page boxes centred with the range text under them (legacy km-monitor). */
  align?: 'between' | 'center';
  /** A feature's own range wording, used when the total is known. */
  formatRange?: (range: { start: number; end: number; total: number }) => string;
  onPageChange: (page: { pageIndex: number; pageSize: number }) => void;
}) {
  const t = useShellTexts().kit;
  const { pageIndex, pageSize, totalCount, pageSizeOptions } = pagination;
  const state = paginatorState(pagination, pageItemCount);
  const go = (index: number) => !disabled && onPageChange({ pageIndex: index, pageSize });
  const showPages = state.known ? state.totalPages > 1 : !(state.isFirst && state.isLast);
  const smallestSize = Math.min(...pageSizeOptions);
  const showSizes =
    pageSizeOptions.length > 1 && !(state.known && (totalCount ?? 0) <= smallestSize);

  return (
    <div
      className={`flex flex-wrap items-center gap-3 border-t border-line px-3 py-3 text-sm ${align === 'center' ? 'flex-col justify-center' : 'justify-between'}`}
    >
      {showPages ? (
        <nav aria-label={t.pagination}>
          <ul className="flex">
            {state.known ? (
              <PageBox
                label={t.firstPage}
                disabled={state.isFirst || disabled}
                onClick={() => go(0)}
              >
                «
              </PageBox>
            ) : null}
            <PageBox
              label={t.previousPage}
              disabled={state.isFirst || disabled}
              onClick={() => go(pageIndex - 1)}
            >
              ‹
            </PageBox>
            {state.known
              ? pageWindow(pageIndex, state.totalPages).map(index => (
                  <PageBox
                    key={index}
                    label={String(index + 1)}
                    active={index === pageIndex}
                    disabled={disabled}
                    onClick={() => index !== pageIndex && go(index)}
                  >
                    {index + 1}
                  </PageBox>
                ))
              : null}
            <PageBox
              label={t.nextPage}
              disabled={state.isLast || disabled}
              onClick={() => go(pageIndex + 1)}
            >
              ›
            </PageBox>
            {state.known ? (
              <PageBox
                label={t.lastPage}
                disabled={state.isLast || disabled}
                onClick={() => go(state.totalPages - 1)}
              >
                »
              </PageBox>
            ) : null}
          </ul>
        </nav>
      ) : align === 'center' ? null : (
        <span />
      )}
      <span className={align === 'center' ? 'text-sm text-ink' : 'text-xs text-ink-muted'}>
        {state.known && formatRange
          ? formatRange({ start: state.start, end: state.end, total: totalCount ?? 0 })
          : state.known
            ? fill(t.range, {
                start: String(state.start),
                end: String(state.end),
                total: String(totalCount),
              })
            : fill(t.rangeOpen, { start: String(state.start), end: String(state.end) })}
      </span>
      {showSizes ? (
        <nav aria-label={t.rowsPerPage}>
          <ul className="flex">
            {pageSizeOptions.map(option => (
              <PageBox
                key={option}
                label={`${t.rowsPerPage}: ${option}`}
                active={option === pageSize}
                disabled={disabled}
                // Keep the first visible row on screen; never below page 0 (Angular went to -1
                // on an empty table).
                onClick={() =>
                  option !== pageSize &&
                  onPageChange({
                    pageIndex: Math.max(0, Math.floor((state.start - 1) / option)),
                    pageSize: option,
                  })
                }
              >
                {option}
              </PageBox>
            ))}
          </ul>
        </nav>
      ) : null}
    </div>
  );
}
